import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, Plus, Upload, XCircle } from 'lucide-react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { useReferenceData } from '../../features/common/referenceData';
import { coconutHarvestsApi, coconutTreesApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { formatDate, formatNumber, shortId, today } from '../../utils/format';
import { cn } from '../../utils/cn';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSFormField, ILMSInput, ILMSSelect } from '../../components/ILMSForm';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { ILMSSpinner } from '../../components/ILMSLoadingState';
import type { CoconutBulkImportResult, CoconutHarvest, CoconutTree, CoconutTreeCreate, CoconutTreeStatus } from '../../types';
import type { ModuleConfig } from '../../features/common/types';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function CoconutBulkImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<CoconutBulkImportResult | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const columnsQuery = useQuery({
    queryKey: ['coconut-trees', 'bulk-import-columns'],
    queryFn: () => coconutTreesApi.bulkImportColumns(),
    enabled: open,
    staleTime: 60 * 60_000,
  });

  const reset = () => {
    setSelectedFile(null);
    setResult(null);
    setDownloadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setDownloadError(null);
    try {
      const blob = await coconutTreesApi.downloadBulkImportTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'coconut_tree_bulk_import_template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(getApiErrorMessage(err, 'Unable to download the template. Please try again.'));
    }
  };

  const importMutation = useMutation({
    mutationFn: () => coconutTreesApi.bulkImport(selectedFile!),
    onSuccess: (data) => {
      setResult(data);
      if (data.succeeded > 0) queryClient.invalidateQueries({ queryKey: ['coconut-trees'] });
    },
  });

  return (
    <ILMSModal
      open={open}
      onClose={handleClose}
      title="Bulk Import Trees / Groups"
      size="lg"
      footer={
        <>
          <ILMSButton variant="ghost" onClick={handleClose}>Close</ILMSButton>
          {!result && (
            <ILMSButton icon={<Upload />} disabled={!selectedFile} loading={importMutation.isPending} onClick={() => importMutation.mutate()}>
              Upload &amp; Import
            </ILMSButton>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-body text-ink-700">
          For onboarding a whole numbered grove at once — fill in one row per tree in the template below, then
          upload it here. Each row is checked independently, so a mistake in one row won't block the rest.
        </p>

        <div className="rounded-md border border-ink-300/20 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="text-card-title">Step 1 — Download the template</p>
            <p className="text-secondary text-ink-500">A ready-to-fill CSV with the correct columns and two example rows.</p>
          </div>
          <ILMSButton variant="outline" icon={<Download />} onClick={downloadTemplate}>
            Download Sample CSV
          </ILMSButton>
        </div>
        {downloadError && <ILMSAlert tone="critical">{downloadError}</ILMSAlert>}

        {!result && (
          <details className="rounded-md border border-ink-300/20 p-4 group">
            <summary className="text-card-title cursor-pointer select-none">
              Not sure what a column means? Click to see what each one is for
            </summary>
            <div className="mt-3 divide-y divide-ink-300/10 border-t border-ink-300/10">
              {(columnsQuery.data ?? []).map((c) => (
                <div key={c.column} className="py-2.5 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-3 gap-y-0.5">
                  <p className="text-secondary font-semibold text-ink-900 flex items-center gap-1.5">
                    {c.column}
                    {c.required && <span className="text-helper-text text-critical-600">*required</span>}
                  </p>
                  <p className="text-secondary text-ink-600">{c.description}</p>
                </div>
              ))}
              {columnsQuery.isLoading && <p className="text-secondary text-ink-400 py-2">Loading…</p>}
            </div>
          </details>
        )}

        {!result && (
          <div className="rounded-md border border-ink-300/20 p-4">
            <p className="text-card-title mb-1">Step 2 — Upload your filled-in CSV</p>
            <p className="text-secondary text-ink-500 mb-3">location must already exist in the system (a name, not an ID) — set it up first under Farm Setup if needed.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              className="text-secondary text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green-50 file:px-3.5 file:py-2 file:text-sm file:font-medium file:text-brand-green-700 hover:file:bg-brand-green-100"
            />
            {importMutation.isError && (
              <ILMSAlert tone="critical" className="mt-3">
                {getApiErrorMessage(importMutation.error, 'Unable to import this file. Please check the format and try again.')}
              </ILMSAlert>
            )}
          </div>
        )}

        {result && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-surface-muted p-3">
                <p className="text-kpi-number text-ink-900">{result.total}</p>
                <p className="text-helper-text text-ink-500">Rows processed</p>
              </div>
              <div className="rounded-md bg-success-50 p-3">
                <p className="text-kpi-number text-success-700">{result.succeeded}</p>
                <p className="text-helper-text text-success-700">Added</p>
              </div>
              <div className="rounded-md bg-critical-50 p-3">
                <p className="text-kpi-number text-critical-700">{result.failed}</p>
                <p className="text-helper-text text-critical-700">Failed</p>
              </div>
            </div>

            {result.succeeded > 0 && (
              <ILMSAlert tone="success">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  Added: {result.created_tree_ids.join(', ')}
                </span>
              </ILMSAlert>
            )}

            {result.errors.length > 0 && (
              <div>
                <p className="text-card-title mb-2 flex items-center gap-2 text-critical-700">
                  <XCircle className="h-4 w-4" /> Rows that need fixing
                </p>
                <div className="rounded-md border border-critical-100 divide-y divide-critical-100">
                  {result.errors.map((e) => (
                    <div key={e.row} className="p-3 text-secondary">
                      <span className="font-semibold text-ink-900">Row {e.row}: </span>
                      <span className="text-ink-700">{e.message}</span>
                    </div>
                  ))}
                </div>
                <ILMSButton variant="outline" className="mt-3" onClick={reset}>
                  Fix and try again
                </ILMSButton>
              </div>
            )}
          </div>
        )}
      </div>
    </ILMSModal>
  );
}

function formatYield(n: number | undefined): string {
  if (!n) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function YieldReportTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [splitGroups, setSplitGroups] = useState(false);
  const reportQuery = useQuery({
    queryKey: ['coconut-yield-report', year, splitGroups],
    queryFn: () => coconutHarvestsApi.yieldReport(year, splitGroups),
  });

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const hasGroupHarvests = (reportQuery.data?.trees ?? []).some((t) => t.is_group);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <label htmlFor="report-year" className="text-label text-ink-700">Year</label>
          <ILMSSelect id="report-year" className="w-32" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </ILMSSelect>
        </div>
        <label className="flex items-center gap-2 text-secondary text-ink-700">
          <input
            type="checkbox"
            checked={splitGroups}
            onChange={(e) => setSplitGroups(e.target.checked)}
            className="h-5 w-5 rounded border-ink-300/50 text-brand-green-700 focus-visible:outline-2 focus-visible:outline-brand-green-500"
          />
          Split group/batch harvests evenly across their trees (estimate)
        </label>
      </div>

      {!splitGroups && hasGroupHarvests && (
        <ILMSAlert tone="info">
          Rows marked <strong>(Group)</strong> are batch harvests across many trees whose individual yields weren't
          counted — they're kept separate rather than guessed at per tree. Tick the box above to instead spread them
          evenly across their trees as an estimate.
        </ILMSAlert>
      )}

      <ILMSCard padded={false}>
        {reportQuery.isLoading ? (
          <div className="p-6"><ILMSSpinner label="Loading yield report…" /></div>
        ) : reportQuery.isError ? (
          <div className="p-6"><ILMSAlert tone="critical">{getApiErrorMessage(reportQuery.error)}</ILMSAlert></div>
        ) : (reportQuery.data?.trees.length ?? 0) === 0 ? (
          <div className="p-6 text-center text-secondary text-ink-500">
            No harvests with a counted nuts_count logged for {year} yet — bulk/uncounted sales (total amount only, no count) don't contribute a number here.
          </div>
        ) : (
          <div className="overflow-x-auto p-4 sm:p-5">
            <table className="w-full border-collapse min-w-[900px]">
              <thead>
                <tr>
                  <th className="text-label text-ink-500 text-left py-2 px-2 sticky left-0 bg-white">Tree</th>
                  {MONTH_LABELS.map((m) => (
                    <th key={m} className="text-label text-ink-500 text-right py-2 px-2">{m}</th>
                  ))}
                  <th className="text-label text-ink-700 text-right py-2 px-2 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {reportQuery.data!.trees.map((t) => (
                  <tr key={t.tree_code} className={cn('border-t border-ink-300/10', t.is_group && 'bg-brand-gold-50/50')}>
                    <td className="text-table-text text-ink-900 font-medium py-2 px-2 sticky left-0 bg-inherit whitespace-nowrap">
                      {t.tree_code}
                    </td>
                    {MONTH_LABELS.map((_, i) => (
                      <td key={i} className="text-table-text text-ink-700 text-right py-2 px-2">{formatYield(t.months[String(i + 1)])}</td>
                    ))}
                    <td className="text-table-text text-ink-900 font-semibold text-right py-2 px-2">{formatYield(t.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-ink-300/20">
                  <td className="text-table-text text-ink-900 font-semibold py-2 px-2 sticky left-0 bg-white">All trees</td>
                  {MONTH_LABELS.map((_, i) => (
                    <td key={i} className="text-table-text text-ink-900 font-semibold text-right py-2 px-2">
                      {formatYield(reportQuery.data!.trees.reduce((sum, t) => sum + (t.months[String(i + 1)] ?? 0), 0))}
                    </td>
                  ))}
                  <td className="text-table-text text-ink-900 font-semibold text-right py-2 px-2">
                    {formatYield(reportQuery.data!.trees.reduce((sum, t) => sum + t.total, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </ILMSCard>
    </div>
  );
}

// Page-vs-tab call: Coconut_Trees (an Assets specialization, REQ-031) and its
// Coconut_Harvests child (REQ-032/WF-10) share one "Coconut" page as tabs.
// Trees needs a custom Add form (not the generic ResourceModule) because
// farm_id is a query parameter on create, not a body field, per openapi-v2.json.

function TreesTab() {
  const queryClient = useQueryClient();
  const { data: refData } = useReferenceData(['farms', 'locations']);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [farmId, setFarmId] = useState('');
  const [values, setValues] = useState<CoconutTreeCreate>({ tree_code: '', status: 'Active' });

  const listQuery = useQuery({
    queryKey: ['coconut-trees', page],
    queryFn: () => coconutTreesApi.list({ page, page_size: 10 }),
  });

  const mutation = useMutation({
    mutationFn: () => coconutTreesApi.create(values, farmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coconut-trees'] });
      setOpen(false);
      setValues({ tree_code: '', status: 'Active' });
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Unable to add this tree/group.')),
  });

  const columns: ILMSTableColumn<CoconutTree>[] = [
    { key: 'code', header: 'Tree/Group Code', primary: true, render: (r) => r.tree_code },
    { key: 'variety', header: 'Variety', render: (r) => r.variety || '—' },
    { key: 'group', header: 'Group/Block', render: (r) => r.tree_group || '—', hideOnTablet: true },
    { key: 'planted', header: 'Planted', render: (r) => formatDate(r.planting_date) },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end gap-2">
        <ILMSButton size="sm" variant="outline" icon={<Upload />} onClick={() => setBulkImportOpen(true)}>Bulk Import</ILMSButton>
        <ILMSButton size="sm" icon={<Plus />} onClick={() => setOpen(true)}>Add Tree/Group</ILMSButton>
      </div>
      <CoconutBulkImportModal open={bulkImportOpen} onClose={() => setBulkImportOpen(false)} />
      <ILMSTable
        columns={columns}
        data={listQuery.data?.items ?? []}
        rowKey={(r) => r.id}
        isLoading={listQuery.isLoading}
        error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
        emptyTitle="No coconut trees/groups registered yet."
      />
      <ILMSModal
        open={open}
        onClose={() => setOpen(false)}
        title="Add Tree / Group"
        footer={
          <>
            <ILMSButton variant="ghost" onClick={() => setOpen(false)}>Cancel</ILMSButton>
            <ILMSButton loading={mutation.isPending} disabled={!values.tree_code || !farmId} onClick={() => { setError(null); mutation.mutate(); }}>
              Add
            </ILMSButton>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
          <ILMSFormField label="Farm" htmlFor="farm" required>
            <ILMSSelect id="farm" value={farmId} onChange={(e) => setFarmId(e.target.value)}>
              <option value="">Select farm…</option>
              {refData.farms.map((f) => (<option key={f.value} value={f.value}>{f.label}</option>))}
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Tree/Group Code" htmlFor="tree_code" required>
            <ILMSInput id="tree_code" value={values.tree_code} onChange={(e) => setValues((v) => ({ ...v, tree_code: e.target.value }))} />
          </ILMSFormField>
          <ILMSFormField label="Variety" htmlFor="variety" optional>
            <ILMSInput id="variety" value={values.variety ?? ''} onChange={(e) => setValues((v) => ({ ...v, variety: e.target.value }))} />
          </ILMSFormField>
          <ILMSFormField label="Tree Group / Block" htmlFor="tree_group" optional>
            <ILMSInput id="tree_group" value={values.tree_group ?? ''} onChange={(e) => setValues((v) => ({ ...v, tree_group: e.target.value }))} />
          </ILMSFormField>
          <ILMSDatePicker label="Planting Date" value={values.planting_date ?? ''} onChange={(e) => setValues((v) => ({ ...v, planting_date: e.target.value }))} />
          <ILMSFormField label="Location" htmlFor="location_id" optional>
            <ILMSSelect id="location_id" value={values.location_id ?? ''} onChange={(e) => setValues((v) => ({ ...v, location_id: e.target.value }))}>
              <option value="">Select location…</option>
              {refData.locations.map((l) => (<option key={l.value} value={l.value}>{l.label}</option>))}
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Status" htmlFor="status" required>
            <ILMSSelect id="status" value={values.status ?? 'Active'} onChange={(e) => setValues((v) => ({ ...v, status: e.target.value as CoconutTreeStatus }))}>
              <option value="Active">Active</option>
              <option value="Removed">Removed</option>
              <option value="Dead">Dead</option>
            </ILMSSelect>
          </ILMSFormField>
        </div>
      </ILMSModal>
    </div>
  );
}

const harvestsConfig: ModuleConfig<CoconutHarvest> = {
  title: 'Harvests', entityLabel: 'harvest', module: 'Coconut', queryKey: 'coconut-harvests', api: coconutHarvestsApi, idKey: 'id',
  referenceKeys: ['coconutTrees', 'coconutTreeGroups'], defaultValues: { harvest_date: today() },
  columns: [
    { key: 'tree', header: 'Tree/Group', primary: true, render: (r) => r.tree_group ? `${r.tree_group} (Group)` : shortId(r.tree_id) },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.harvest_date) },
    { key: 'nuts', header: 'Nuts', align: 'right', render: (r) => r.nuts_count ?? '—' },
    { key: 'sale_type', header: 'Sold As', render: (r) => r.sale_type ? <ILMSStatusBadge status={r.sale_type} size="sm" /> : '—', hideOnTablet: true },
    { key: 'price', header: 'Price/Nut (₹)', align: 'right', render: (r) => r.price_per_nut ? formatNumber(r.price_per_nut, 2) : '—' },
    { key: 'total_sale', header: 'Sale Amount (₹)', align: 'right', render: (r) => r.total_sale_amount ? formatNumber(r.total_sale_amount, 2) : '—' },
    { key: 'weight', header: 'Total Weight (kg)', align: 'right', render: (r) => formatNumber(r.total_weight_kg, 2), hideOnTablet: true },
    { key: 'grade', header: 'Quality Grade', render: (r) => r.quality_grade || '—', hideOnTablet: true },
  ],
  fields: [
    { name: 'tree_id', label: 'Single Tree', type: 'select', section: 'Harvest', optional: true, optionsKey: 'coconutTrees',
      helperText: 'Fill in EITHER this (one tree, individually counted) OR Tree Group below (a batch across many trees) — not both.' },
    { name: 'tree_group', label: 'OR: Tree Group / Batch', type: 'select', section: 'Harvest', optional: true, optionsKey: 'coconutTreeGroups',
      helperText: "Use this when you harvested/sold many trees together and don't have each tree's individual count." },
    { name: 'harvest_date', label: 'Date', type: 'date', section: 'Harvest', required: true },
    { name: 'nuts_count', label: 'Nuts Count', type: 'number', section: 'Yield', optional: true, min: 0,
      helperText: 'Leave blank if you sold this as one uncounted bulk batch — just fill in Sale Amount below instead.' },
    { name: 'total_weight_kg', label: 'Total Weight (kg)', type: 'decimal', section: 'Yield', optional: true, min: 0 },
    { name: 'quality_grade', label: 'Quality Grade', type: 'text', section: 'Yield', optional: true },
    { name: 'harvest_cycle_no', label: 'Harvest Cycle #', type: 'number', section: 'Yield', optional: true, min: 0 },
    { name: 'sale_type', label: 'Sold As', type: 'select', section: 'Sale', optional: true, staticOptions: [
      { value: 'TenderCoconut', label: 'Tender Coconut' }, { value: 'Coconut', label: 'Coconut' },
    ] },
    { name: 'price_per_nut', label: 'Average Price per Nut (₹)', type: 'decimal', section: 'Sale', optional: true, min: 0 },
    { name: 'total_sale_amount', label: 'Total Sale Amount (₹)', type: 'decimal', section: 'Sale', optional: true, min: 0,
      helperText: 'Use this when nuts were sold as an uncounted bulk batch instead of by individual price.' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Yield', optional: true, fullWidth: true },
  ],
};

export default function CoconutPage() {
  return (
    <TabbedPage
      title="Coconut"
      subtitle="Tree/group registry and harvest yield history (WF-10)."
      tabs={[
        { id: 'trees', label: 'Trees / Groups', render: () => <TreesTab /> },
        { id: 'harvests', label: 'Harvests', render: () => <ResourceModule config={harvestsConfig} /> },
        { id: 'yield-report', label: 'Yield Report', render: () => <YieldReportTab /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Coconut', harvestsConfig);
